import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  ArrowUpRight,
  CalendarDays,
  Linkedin,
  Twitter,
  Mail,
  Link as LinkIcon,
  Check,
  Sparkles,
  Compass,
} from "lucide-react";
import useSeo from "../lib/useSeo";
import {
  SERVICE_LINKS_BY_CATEGORY,
  buildCanonical,
} from "../lib/insightsLinks";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

export default function InsightArticle() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    let cancelled = false;
    (async () => {
      try {
        const [a, r] = await Promise.all([
          axios.get(`${API}/insights/${slug}`),
          axios.get(`${API}/insights/${slug}/related`).catch(() => ({ data: { items: [] } })),
        ]);
        if (cancelled) return;
        setArticle(a.data);
        setRelated(r.data?.items || []);
      } catch {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
        window.scrollTo({ top: 0, behavior: "instant" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // SEO meta + JSON-LD schema for this article
  const canonical = article ? buildCanonical(`/insights/${article.slug}`) : null;
  useSeo(
    article
      ? {
          title:
            article.meta_title ||
            `${article.title} — Linked2Marketing Insights`,
          description: article.meta_description || article.excerpt,
          canonical,
          ogImage: article.featured_image || undefined,
          ogType: "article",
          jsonLd: [
            {
              "@context": "https://schema.org",
              "@type": "BlogPosting",
              headline: article.title,
              description: article.meta_description || article.excerpt,
              image: article.featured_image ? [article.featured_image] : undefined,
              datePublished: article.published_at,
              dateModified: article.updated_at || article.published_at,
              author: {
                "@type": "Organization",
                name: article.author || "Linked2Marketing",
                url: canonical ? canonical.split("/insights/")[0] : undefined,
              },
              publisher: {
                "@type": "Organization",
                name: "Linked2Marketing",
                logo: {
                  "@type": "ImageObject",
                  url:
                    canonical
                      ? `${canonical.split("/insights/")[0]}/favicon-192.png`
                      : undefined,
                },
              },
              mainEntityOfPage: {
                "@type": "WebPage",
                "@id": canonical,
              },
              articleSection: article.category,
              inLanguage: "en-GB",
            },
            {
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Home",
                  item: canonical ? canonical.split("/insights/")[0] : undefined,
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "Insights",
                  item: canonical
                    ? `${canonical.split("/insights/")[0]}/insights`
                    : undefined,
                },
                {
                  "@type": "ListItem",
                  position: 3,
                  name: article.title,
                  item: canonical,
                },
              ],
            },
          ],
        }
      : {}
  );

  if (loading) {
    return (
      <div className="pt-40 pb-32 mx-auto max-w-3xl px-6">
        <div className="h-8 w-32 rounded-full bg-white/[0.04] animate-pulse" />
        <div className="mt-8 h-12 w-3/4 rounded bg-white/[0.04] animate-pulse" />
        <div className="mt-6 h-72 rounded-3xl bg-white/[0.03] animate-pulse" />
      </div>
    );
  }

  if (notFound || !article) {
    return (
      <div className="pt-40 pb-32 mx-auto max-w-2xl px-6 text-center">
        <h1 className="font-display text-3xl text-mute-100">
          Article not found.
        </h1>
        <p className="mt-4 text-mute-200">
          The piece you were looking for may have been moved or unpublished.
        </p>
        <button
          onClick={() => navigate("/insights")}
          className="btn-secondary mt-8 inline-flex"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Insights
        </button>
      </div>
    );
  }

  const shareUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/insights/${article.slug}`
      : `/insights/${article.slug}`;
  const shareTitle = encodeURIComponent(article.title);
  const shareLink = encodeURIComponent(shareUrl);

  return (
    <article className="pt-32 sm:pt-36 pb-24" data-testid="page-insight-article">
      {/* HEADER */}
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <Link
          to="/insights"
          data-testid="insight-back-link"
          className="inline-flex items-center gap-1.5 text-[12.5px] uppercase tracking-[0.22em] text-mute-300 hover:text-gold transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          All insights
        </Link>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center rounded-full border border-gold/30 bg-gold/[0.06] px-3 py-1 text-[11px] uppercase tracking-[0.22em] text-gold">
            {article.category}
          </span>
          <span className="inline-flex items-center gap-2 text-[12px] text-mute-300 tracking-[0.05em]">
            <CalendarDays className="w-3.5 h-3.5 text-gold/80" />
            {formatDate(article.published_at)}
          </span>
        </div>

        <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-[60px] leading-[1.05] tracking-tight text-mute-100">
          {article.title}
        </h1>
        {article.excerpt && (
          <p className="mt-7 text-[18px] leading-[1.6] text-mute-200 max-w-3xl">
            {article.excerpt}
          </p>
        )}
      </div>

      {/* HERO IMAGE */}
      {article.featured_image && (
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 mt-12">
          <div className="relative rounded-3xl overflow-hidden aspect-[16/9] ring-1 ring-white/10">
            <img
              src={article.featured_image}
              alt={article.title}
              className="absolute inset-0 w-full h-full object-cover"
              loading="eager"
            />
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "linear-gradient(180deg, rgba(10,12,14,0.35) 0%, rgba(10,12,14,0.10) 50%, rgba(10,12,14,0.60) 100%)",
              }}
            />
          </div>
        </div>
      )}

      {/* BODY + SHARE */}
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        {/* Share rail */}
        <aside className="lg:col-span-2 lg:sticky lg:top-32">
          <Author article={article} />
          <div className="hairline my-7 lg:my-8" />
          <ShareRail shareUrl={shareUrl} shareTitle={shareTitle} shareLink={shareLink} />
        </aside>

        {/* Body */}
        <div className="lg:col-span-10">
          <ArticleBody body={article.body} />
        </div>
      </div>

      {/* RELATED + CTA */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-24">
        <InternalLinks article={article} />
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-16">
        <ArticleCta />
      </div>

      {related.length > 0 && (
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-24">
          <h3 className="font-display text-2xl sm:text-3xl text-mute-100 leading-[1.15]">
            Related insights
          </h3>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
            {related.map((r, i) => (
              <Link
                key={r.slug}
                to={`/insights/${r.slug}`}
                data-testid={`insight-related-${i}`}
                className="group rounded-2xl glass card-hover overflow-hidden flex flex-col"
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  {r.featured_image && (
                    <img
                      src={r.featured_image}
                      alt={r.title}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1100ms] ease-out group-hover:scale-[1.04]"
                    />
                  )}
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background:
                        "linear-gradient(180deg, rgba(10,12,14,0.35) 0%, rgba(10,12,14,0.05) 40%, rgba(10,12,14,0.70) 100%)",
                    }}
                  />
                </div>
                <div className="p-6 flex flex-col gap-3 flex-1">
                  <span className="text-[10.5px] uppercase tracking-[0.22em] text-gold">
                    {r.category}
                  </span>
                  <h4 className="font-display text-[17px] leading-[1.25] text-mute-100">
                    {r.title}
                  </h4>
                  <div className="mt-auto pt-3 inline-flex items-center gap-1.5 text-[12.5px] text-gold/90 group-hover:translate-x-0.5 transition-transform">
                    Read article <ArrowUpRight className="w-3.5 h-3.5" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </article>
  );
}

function Author({ article }) {
  return (
    <div className="flex items-start gap-3">
      <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl border border-gold/30 bg-gold/[0.06] text-gold flex-none">
        <Sparkles className="w-[18px] h-[18px]" />
      </span>
      <div>
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-gold">
          Author
        </div>
        <div className="mt-1 text-[14px] text-mute-100 font-medium leading-tight">
          {article.author || "Linked2Marketing"}
        </div>
        <div className="mt-1 text-[12px] text-mute-300">
          Digital infrastructure studio
        </div>
      </div>
    </div>
  );
}

function ShareRail({ shareUrl, shareTitle, shareLink }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      // ignore
    }
  };
  const items = [
    {
      label: "LinkedIn",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${shareLink}`,
      icon: Linkedin,
      testid: "share-linkedin",
    },
    {
      label: "X / Twitter",
      href: `https://twitter.com/intent/tweet?url=${shareLink}&text=${shareTitle}`,
      icon: Twitter,
      testid: "share-twitter",
    },
    {
      label: "Email",
      href: `mailto:?subject=${shareTitle}&body=${shareLink}`,
      icon: Mail,
      testid: "share-email",
    },
  ];
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-[0.22em] text-mute-300">
        Share
      </div>
      <div className="mt-3 flex lg:flex-col gap-2">
        {items.map((it) => (
          <a
            key={it.label}
            href={it.href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Share on ${it.label}`}
            data-testid={it.testid}
            className="group inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.025] backdrop-blur-md px-3.5 py-2 text-[12.5px] text-mute-100 hover:border-gold/40 hover:text-gold hover:bg-gold/[0.05] transition-colors"
          >
            <it.icon className="w-3.5 h-3.5 text-gold/80 group-hover:text-gold transition-colors" />
            <span>{it.label}</span>
          </a>
        ))}
        <button
          type="button"
          onClick={copy}
          data-testid="share-copy"
          className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.025] backdrop-blur-md px-3.5 py-2 text-[12.5px] text-mute-100 hover:border-gold/40 hover:text-gold hover:bg-gold/[0.05] transition-colors"
        >
          {copied ? (
            <Check className="w-3.5 h-3.5 text-gold" />
          ) : (
            <LinkIcon className="w-3.5 h-3.5 text-gold/80" />
          )}
          <span>{copied ? "Copied" : "Copy link"}</span>
        </button>
      </div>
    </div>
  );
}

function ArticleBody({ body }) {
  // Render as paragraphs split on blank lines, in calm editorial style.
  const paragraphs = (body || "").split(/\n\s*\n/).filter(Boolean);
  return (
    <div
      className="prose-l2m"
      data-testid="insight-article-body"
    >
      {paragraphs.map((p, i) => (
        <p
          key={i}
          className="text-[17px] sm:text-[18px] leading-[1.75] text-mute-200 mt-7 first:mt-0"
        >
          {p}
        </p>
      ))}
    </div>
  );
}

function InternalLinks({ article }) {
  const links = SERVICE_LINKS_BY_CATEGORY[article.category] || [];
  if (!links.length) return null;
  return (
    <aside
      data-testid="insight-internal-links"
      className="relative rounded-3xl glass overflow-hidden p-8 sm:p-10"
    >
      <div
        className="absolute -top-24 left-1/2 -translate-x-1/2 w-[460px] h-[460px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(closest-side, rgba(199,166,106,0.10), transparent 70%)",
        }}
      />
      <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
        <div className="lg:col-span-4">
          <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-gold">
            <Compass className="w-3.5 h-3.5" /> Continue exploring
          </span>
          <h3 className="mt-4 font-display text-2xl sm:text-3xl text-mute-100 leading-[1.15]">
            Closely related to this piece
          </h3>
          <p className="mt-4 text-[14px] leading-[1.7] text-mute-200 max-w-md">
            Quiet on-ramps to the relevant areas of our work and writing.
          </p>
        </div>
        <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {links.map((l, i) => (
            <Link
              key={`${l.to}-${l.label}-${i}`}
              to={l.to}
              data-testid={`insight-internal-link-${i}`}
              className="group rounded-2xl border border-white/8 bg-white/[0.025] backdrop-blur-md p-5 hover:border-gold/40 hover:bg-gold/[0.05] transition-colors"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[10.5px] uppercase tracking-[0.22em] text-gold/80">
                    {l.isCategory ? "Insights" : "Service"}
                  </div>
                  <div className="mt-2 text-[15px] text-mute-100 font-medium leading-tight">
                    {l.label}
                  </div>
                  <p className="mt-2 text-[13px] text-mute-200 leading-[1.6]">
                    {l.blurb}
                  </p>
                </div>
                <ArrowUpRight className="w-4 h-4 text-mute-300 group-hover:text-gold transition-colors flex-none" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </aside>
  );
}


function ArticleCta() {
  return (
    <aside
      data-testid="insight-cta-block"
      className="relative rounded-3xl glass-strong overflow-hidden p-10 sm:p-14"
    >
      <div
        className="absolute -top-32 -right-24 w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(closest-side, rgba(199,166,106,0.14), transparent 70%)",
        }}
      />
      <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
        <div className="lg:col-span-8">
          <span className="eyebrow text-gold">Considered next step</span>
          <h3 className="mt-5 font-display text-3xl sm:text-4xl lg:text-[44px] leading-[1.08] tracking-tight text-mute-100">
            Need help with websites, CRM systems, automation, or AI visibility?
          </h3>
          <p className="mt-5 text-[15.5px] leading-[1.75] text-mute-200 max-w-2xl">
            Book a free, low-pressure strategy discussion with Linked2Marketing
            — a quiet conversation about where your digital systems could feel
            calmer, clearer, and more connected.
          </p>
        </div>
        <div className="lg:col-span-4 lg:justify-self-end">
          <Link
            to="/#enquiry"
            data-testid="insight-cta-link"
            className="btn-primary"
          >
            Start a conversation <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </aside>
  );
}
